import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import './TradeTracker.css';

/**
 * TRADE TRACKER - Sheet-Based Display
 * Displays Google Sheets data directly, exactly as it appears in the sheet.
 * No calculations - just fetch and display.
 */
function TradeTracker({ guruSlug }) {
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingSheet, setLoadingSheet] = useState(false);
  const [error, setError] = useState(null);

  // Fetch products with sheet_url
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const response = await api.get(`/gurus/${guruSlug}/products`);
        const trackingProducts = response.data.filter(p => p.sheet_url);
        setProducts(trackingProducts);

        // Auto-select first product if available
        if (trackingProducts.length > 0) {
          fetchSheetHtml(trackingProducts[0]);
        }
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [guruSlug]);

  // Parse HTML table from Google Sheets
  const parseHtmlTable = (html) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const table = doc.querySelector('table');

    if (!table) return null;

    const rows = table.querySelectorAll('tr');
    const result = [];

    rows.forEach(row => {
      const cells = row.querySelectorAll('td, th');
      const rowData = [];
      cells.forEach(cell => rowData.push((cell.textContent || '').trim()));
      result.push(rowData);
    });

    if (result.length === 0) return null;

    // Find header row (first row with 3+ non-empty cells)
    let headerIdx = 0;
    for (let i = 0; i < Math.min(result.length, 15); i++) {
      const nonEmptyCells = result[i].filter(c => c && c.trim() !== '');
      if (nonEmptyCells.length >= 3) {
        headerIdx = i;
        break;
      }
    }

    const headers = result[headerIdx];

    // Get data rows (skip header and any rows before it)
    const dataRows = result.slice(headerIdx + 1).filter(row =>
      row.some(cell => cell && cell.trim() !== '')
    );

    // Find columns that have actual data (filter out empty columns)
    const nonEmptyColIndices = [];
    for (let colIdx = 0; colIdx < headers.length; colIdx++) {
      const hasData = headers[colIdx]?.trim() ||
        dataRows.some(row => row[colIdx]?.trim());
      if (hasData) {
        nonEmptyColIndices.push(colIdx);
      }
    }

    // Filter headers and rows to only include non-empty columns
    const filteredHeaders = nonEmptyColIndices.map(i => headers[i] || '');
    const filteredRows = dataRows.map(row =>
      nonEmptyColIndices.map(i => row[i] || '')
    );

    return { headers: filteredHeaders, rows: filteredRows };
  };

  // Fetch sheet HTML when product selected
  const fetchSheetHtml = useCallback(async (product) => {
    setSelectedProduct(product);
    setLoadingSheet(true);
    setError(null);
    setTableData(null);

    try {
      const response = await api.get(`/products/${product.id}/sheet-html`);

      if (response.data.success && response.data.html) {
        const parsed = parseHtmlTable(response.data.html);
        if (parsed) {
          setTableData(parsed);
        } else {
          setError('Could not parse sheet data');
        }
      } else {
        setError('Failed to fetch sheet data');
      }
    } catch (err) {
      console.error('Error fetching sheet:', err);
      setError(err.response?.data?.detail || 'Failed to load sheet data');
    } finally {
      setLoadingSheet(false);
    }
  }, []);

  // Get cell CSS class based on value (for coloring positive/negative)
  const getCellClass = (value, header) => {
    if (!value) return '';

    // Check if this looks like a percentage or dollar value
    const cleaned = value.replace(/[()%$,\s]/g, '').replace(/^\+/, '');
    const num = parseFloat(cleaned);

    // Only color if it's a number and looks like a return/P&L column
    const isReturnColumn = header && (
      header.toLowerCase().includes('return') ||
      header.toLowerCase().includes('p/l') ||
      header.toLowerCase().includes('profit') ||
      header.toLowerCase().includes('gain') ||
      header.toLowerCase().includes('loss') ||
      header.toLowerCase().includes('%')
    );

    const looksLikePercent = value.includes('%') ||
      value.match(/^[(\-+]?\d+\.?\d*[)%]?$/);

    if (!isNaN(num) && (isReturnColumn || looksLikePercent)) {
      if (num > 0) return 'positive';
      if (num < 0) return 'negative';
    }

    return '';
  };

  // Check if cell is a status cell
  const getStatusClass = (value) => {
    const lower = value?.toLowerCase() || '';
    if (lower === 'open' || lower === 'active') return 'status-open';
    if (lower === 'closed' || lower === 'complete' || lower === 'done') return 'status-closed';
    return '';
  };

  const getPublishedSheetUrl = (csvUrl) => {
    if (!csvUrl) return '#';
    try {
      const url = new URL(csvUrl);
      url.searchParams.delete('output');
      url.searchParams.delete('single');
      url.pathname = url.pathname.replace(/\/pub$/, '/pubhtml');
      return url.toString();
    } catch {
      return csvUrl.replace(/\/pub\?.*$/, '/pubhtml');
    }
  };

  if (loading) {
    return <div className="tracker-loading">Loading Trade Tracker...</div>;
  }

  if (products.length === 0) {
    return <div className="tracker-empty">No products with tracking enabled.</div>;
  }

  return (
    <div className="trade-tracker">
      {/* Header with LIVE badge */}
      <div className="tracker-header">
        <h2>Trade Tracker</h2>
        <div className="live-badge">
          <span className="live-dot"></span>
          LIVE
        </div>
      </div>

      {/* Product Cards Grid */}
      <div className="products-grid">
        {products.map(product => (
          <div
            key={product.id}
            className={`product-card ${selectedProduct?.id === product.id ? 'active' : ''}`}
            onClick={() => fetchSheetHtml(product)}
          >
            {product.logo_url ? (
              <img src={product.logo_url} alt={product.name} className="product-logo" />
            ) : (
              <div className="product-logo-placeholder">
                {product.name.substring(0, 2).toUpperCase()}
              </div>
            )}
            <div className="product-name">{product.name}</div>
          </div>
        ))}
      </div>

      {/* Sheet Table */}
      {selectedProduct && (
        <div className="sheet-container">
          <div className="sheet-header">
            <h3>{selectedProduct.name}</h3>
            {selectedProduct.sheet_url && (
              <a
                href={getPublishedSheetUrl(selectedProduct.sheet_url)}
                target="_blank"
                rel="noopener noreferrer"
                className="open-sheet-link"
              >
                Open Sheet →
              </a>
            )}
          </div>

          {loadingSheet ? (
            <div className="tracker-loading">Loading sheet data...</div>
          ) : error ? (
            <div className="tracker-error">{error}</div>
          ) : tableData ? (
            <div className="trades-table-wrapper">
              <table className={`trades-table ${tableData.headers.length >= 10 ? 'compact-mode' : ''}`}>
                <thead>
                  <tr>
                    {tableData.headers.map((h, i) => (
                      <th key={i}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {tableData.rows.map((row, ri) => (
                    <tr key={ri}>
                      {row.map((cell, ci) => {
                        const header = tableData.headers[ci];
                        const cellClass = getCellClass(cell, header);
                        const statusClass = getStatusClass(cell);

                        return (
                          <td
                            key={ci}
                            className={`${cellClass} ${statusClass}`.trim()}
                          >
                            {statusClass ? (
                              <span className={`status-badge ${statusClass}`}>
                                {cell.toUpperCase()}
                              </span>
                            ) : (
                              cell
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="tracker-empty">Select a product to view trade data.</div>
          )}
        </div>
      )}
    </div>
  );
}

export default TradeTracker;
